import React, { useEffect } from "react";
import { EditOutlined } from "@ant-design/icons";
import { Button, Form, Image, Input } from "antd";
import ButtonWrapper from "../../components/reuseComp/ButtonWrapper";
import UploadFoodImage from "../../../lib/UploadFoodImage";

const { TextArea } = Input;

interface TaxesSimplifiedData {
  heading?: string;
  description?: string;
  image?: string;
}

interface TaxesSimplifiedProps {
  isEditing: boolean;
  setIsEditing: (value: boolean) => void;
  handleCancel: () => void;
  handleSave: () => void;
  form: any;
  taxesSimplifiedData?: TaxesSimplifiedData;
  loading?: boolean;
}

const TaxesSimplified: React.FC<TaxesSimplifiedProps> = ({
  isEditing,
  setIsEditing,
  handleCancel,
  handleSave,
  form,
  taxesSimplifiedData,
  loading,
}) => {
  useEffect(() => {
    if (taxesSimplifiedData) {
      form.setFieldsValue({
        taxesSimplified: {
          heading: taxesSimplifiedData.heading || "",
          description: taxesSimplifiedData.description || "",
          image: taxesSimplifiedData.image
            ? taxesSimplifiedData.image
            : "",
        },
      });
    } else {
      form.resetFields();
    }
  }, [taxesSimplifiedData, form]);

  return (
    <div>
      {!isEditing ? (
        <>
          {/* Desktop View */}

          <div className="rounded-[20px] overflow-hidden hidden sm:block">
            <table className="min-w-full border-collapse">
              <thead>
                <tr className="bg-primary text-white">
                  <th className="py-3 px-4 text-left">Image</th>
                  <th className="py-3 px-4 text-left">Heading</th>
                  <th className="py-3 px-4 text-left">Description</th>
                  <th className="py-3 px-4 text-end">Action</th>
                </tr>
              </thead>

              <tbody>
                {taxesSimplifiedData ? (
                  <tr className="bg-[#F9F9F9] border-b-2 border-[#e9e8e8]">
                    <td className="py-3 px-4 w-[120px]">
                      <Image
                        src={
                          taxesSimplifiedData?.image || "/placeholder.jpg"
                        }
                        height={50}
                        width={120}
                        className="object-contain"
                        alt="Taxes Simplified"
                      />
                    </td>

                    <td className="py-3 px-4">
                      {taxesSimplifiedData?.heading || "—"}
                    </td>

                    <td className="py-3 px-4">
                      {taxesSimplifiedData?.description || "—"}
                    </td>

                    <td className="py-3 px-4 text-primary">
                      <div className="flex justify-end">
                        <EditOutlined
                          onClick={() => setIsEditing(true)}
                          className="cursor-pointer text-[20px]"
                        />
                      </div>
                    </td>
                  </tr>
                ) : (
                  <tr className="bg-[#F9F9F9] border-b-2 border-[#e9e8e8]">
                    <td colSpan={4} className="text-center py-6">
                      <Button type="dashed" onClick={() => setIsEditing(true)}>
                        Add Data
                      </Button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile View */}

          <div className="sm:hidden">
            {taxesSimplifiedData ? (
              <div className="bg-[#F9F9F9] border rounded-lg p-4 space-y-3">
                <div>
                  <span className="font-semibold text-primary block mb-1">
                    Image
                  </span>
                  <Image
                    src={
                      taxesSimplifiedData?.image || "/placeholder.jpg"
                    }
                    height={70}
                    width={70}
                    alt="Taxes Simplified"
                  />
                </div>

                <div>
                  <span className="font-semibold text-primary block mb-1">
                    Heading
                  </span>
                  {taxesSimplifiedData?.heading}
                </div>

                <div>
                  <span className="font-semibold text-primary block mb-1">
                    Description
                  </span>
                  {taxesSimplifiedData?.description}
                </div>

                <EditOutlined
                  onClick={() => setIsEditing(true)}
                  className="cursor-pointer text-[18px]"
                />
              </div>
            ) : (
              <div className="flex justify-center border rounded-lg p-4">
                <Button type="dashed" onClick={() => setIsEditing(true)}>
                  Add Data
                </Button>
              </div>
            )}
          </div>
        </>
      ) : (
        /* Edit Mode */

        <div>
          <div className="bg-[#F9F9F9] p-4 sm:p-6 rounded-lg sm:rounded-2xl shadow-sm">
            <Form layout="vertical" form={form}>
              <Form.Item
                label="Heading"
                name={["taxesSimplified", "heading"]}
                rules={[{ required: true, message: "Please enter heading" }]}
              >
                <Input placeholder="Enter heading" />
              </Form.Item>

              <Form.Item
                label="Description"
                name={["taxesSimplified", "description"]}
                rules={[
                  { required: true, message: "Please enter description" },
                ]}
              >
                <TextArea rows={3} placeholder="Enter description" />
              </Form.Item>

              <Form.Item
                label="Image"
                name={["taxesSimplified", "image"]}
                rules={[
                  { required: true, message: "Please upload an image" },
                ]}
              >
                <UploadFoodImage maxUploadCount={1} bannerAspect={1.5} />
              </Form.Item>
            </Form>
          </div>

          <div className="flex gap-3 mt-6 sticky bottom-5">
            <ButtonWrapper
              type="primary"
              onClick={handleSave}
              loading={loading}
            >
              Save
            </ButtonWrapper>

            <ButtonWrapper onClick={handleCancel}>
              Cancel
            </ButtonWrapper>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaxesSimplified;